import { useState } from 'react';
import { LoadingSpinner } from '../../components/ui/loading-spinner';
import { Pagination } from '../../components/ui/pagination';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { useOrganizationSettings } from '../../hooks/use-organization';
import { useDepartments } from '../../hooks/use-department';
import {
  useOrganizationTests,
  type TestReportMember,
} from '../../hooks/use-organization-tests';
import {
  getCurrentTestPeriodRange,
  type DateRange,
} from '../../helpers/get-current-test-period-range';
import { TestsFilterBar } from './tests-filter-bar';
import { MemberRow } from './member-row';

const EMPTY_RANGE: DateRange = { startDate: '', endDate: '' };

interface OrganizationTestsTabProps {
  organizationId: number | undefined;
  pageSize: number;
  isActive: boolean;
}

interface MembersTableProps {
  members: TestReportMember[];
  organizationId: number | undefined;
  range: DateRange;
  expandedMemberId: number | null;
  onToggle: (memberId: number) => void;
}

function MembersTable({
  members,
  organizationId,
  range,
  expandedMemberId,
  onToggle,
}: MembersTableProps) {
  if (members.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">
        No people match these filters
      </p>
    );
  }
  return (
    <div className="divide-y">
      {members.map((member) => (
        <MemberRow
          key={member.id}
          member={member}
          organizationId={organizationId}
          range={range}
          isExpanded={expandedMemberId === member.id}
          onToggle={() => onToggle(member.id)}
        />
      ))}
    </div>
  );
}

export function OrganizationTestsTab({
  organizationId,
  pageSize,
  isActive,
}: OrganizationTestsTabProps) {
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState<DateRange | null>(null);
  const [applied, setApplied] = useState<DateRange | null>(null);
  const [teamIds, setTeamIds] = useState<number[]>([]);
  const [expandedMemberId, setExpandedMemberId] = useState<number | null>(null);

  const { data: settings } = useOrganizationSettings(
    organizationId ?? 0,
    !!organizationId && isActive,
  );
  const { data: teams } = useDepartments(organizationId ?? 0, isActive);
  const teamOptions = (teams?.departments ?? []).map((department) => ({
    value: department.id,
    label: department.name,
  }));

  const defaultRange = settings
    ? getCurrentTestPeriodRange(settings.testPeriod)
    : null;
  const appliedRange = applied ?? defaultRange;
  const {
    data: report,
    isLoading,
    error,
  } = useOrganizationTests(
    organizationId,
    page,
    pageSize,
    { ...(appliedRange ?? EMPTY_RANGE), departmentIds: teamIds },
    isActive && !!appliedRange,
  );

  const showFirstPage = () => {
    setPage(1);
    setExpandedMemberId(null);
  };
  const applyDraft = () => {
    setApplied(draft);
    showFirstPage();
  };
  const resetDates = () => {
    setDraft(null);
    setApplied(null);
    showFirstPage();
  };
  const changeTeams = (nextTeamIds: number[]) => {
    setTeamIds(nextTeamIds);
    showFirstPage();
  };
  const changePage = (nextPage: number) => {
    setPage(nextPage);
    setExpandedMemberId(null);
  };
  const toggleMember = (memberId: number) => {
    setExpandedMemberId(expandedMemberId === memberId ? null : memberId);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tests</CardTitle>
        <p className="text-sm text-muted-foreground">
          Average test score for everyone in this organization
        </p>
      </CardHeader>
      <CardContent>
        {appliedRange && (
          <TestsFilterBar
            draft={draft ?? appliedRange}
            applied={appliedRange}
            onDraftChange={setDraft}
            onApply={applyDraft}
            onReset={resetDates}
            teamOptions={teamOptions}
            selectedTeamIds={teamIds}
            onTeamsChange={changeTeams}
          />
        )}
        <div className="overflow-x-auto">
          <div className="min-w-[480px]">
            <div className="grid grid-cols-[1fr_80px_120px_40px] gap-4 px-4 py-2 text-sm font-medium text-muted-foreground border-b">
              <span>Person</span>
              <span className="text-right">Tests</span>
              <span className="text-right">Avg Score</span>
              <span />
            </div>
            {isLoading || !report ? (
              <div className="flex justify-center py-8">
                {error ? (
                  <p className="text-sm text-muted-foreground">
                    Could not load tests. Please try again.
                  </p>
                ) : (
                  <LoadingSpinner />
                )}
              </div>
            ) : (
              <MembersTable
                members={report.members}
                organizationId={organizationId}
                range={appliedRange ?? EMPTY_RANGE}
                expandedMemberId={expandedMemberId}
                onToggle={toggleMember}
              />
            )}
          </div>
        </div>
        <Pagination
          currentPage={page}
          totalPages={report?.totalPages ?? 0}
          onPageChange={changePage}
        />
      </CardContent>
    </Card>
  );
}
